import React from 'react';
import { Avatar } from '@biq/apex-ui';
import {
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Typography,
} from '@mui/material';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BlastAvatarImg } from '../../../assets';
import { ChatMessage, MessageType } from '../ask-blast-helper';
import { messageStyles } from './blast-ai-chat-helper';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export const Message = ({
    type,
    title,
    value,
}: ChatMessage): React.JSX.Element => {
    const getAvatarFromMessageType = (): React.JSX.Element => {
        switch (type) {
            case 'user':
                return <Avatar initial={'Q'} sizes={'large'} />;
            case 'pending':
                return (
                    <CircularProgress variant='indeterminate' disableShrink />
                );
            case 'error':
                return <ErrorOutlineIcon sx={messageStyles.errorIcon} />;
            default:
                return (
                    <Avatar
                        sx={messageStyles.systemAvatar}
                        image={BlastAvatarImg}
                        sizes={'large'}
                    />
                );
        }
    };

    const getSystemMessage = (): React.JSX.Element => {
        return (
            <Card elevation={0} sx={messageStyles.systemMessage}>
                <CardContent sx={messageStyles.cardContent}>
                    <Grid
                        sx={
                            type === 'pending'
                                ? messageStyles.pendingCardContentContainer
                                : messageStyles.cardContentContainer
                        }
                    >
                        <Grid sx={messageStyles.avatarContainer}>
                            {getAvatarFromMessageType()}
                        </Grid>
                        <Grid sx={messageStyles.textContainer}>
                            {title && (
                                <Typography sx={messageStyles.messageTitle}>
                                    {title}
                                </Typography>
                            )}
                            <Typography
                                sx={
                                    type === 'pending'
                                        ? messageStyles.pendingMessageValue
                                        : messageStyles.messageValue
                                }
                            >
                                <Markdown
                                    remarkPlugins={[remarkGfm]}
                                    className='markdown'
                                >
                                    {value}
                                </Markdown>
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    const getSystemErrorMessage = (): React.JSX.Element => {
        return (
            <Card elevation={0} sx={messageStyles.errorMessage}>
                <CardContent sx={messageStyles.cardContent}>
                    <Grid sx={messageStyles.errorCardContentContainer}>
                        <Grid sx={messageStyles.errorIconContainer}>
                            {getAvatarFromMessageType()}
                        </Grid>
                        <Grid sx={messageStyles.textContainer}>
                            <Typography sx={messageStyles.errorMessageValue}>
                                {value}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    const getSystemMessageFromType = (
        messageType: MessageType
    ): React.JSX.Element => {
        if (messageType === 'error') {
            return getSystemErrorMessage();
        } else {
            return getSystemMessage();
        }
    };

    return (
        <Grid sx={messageStyles.mainContainer}>
            {type === 'user' ? (
                <Card elevation={1} sx={messageStyles.userMessage}>
                    <CardContent sx={messageStyles.cardContent}>
                        <Grid sx={messageStyles.cardContentContainer}>
                            <Grid sx={messageStyles.avatarContainer}>
                                {getAvatarFromMessageType()}
                            </Grid>
                            <Grid sx={messageStyles.textContainer}>
                                {title && (
                                    <Typography sx={messageStyles.messageTitle}>
                                        {title}
                                    </Typography>
                                )}
                                <Typography sx={messageStyles.messageValue}>
                                    {value}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ) : (
                getSystemMessageFromType(type)
            )}
        </Grid>
    );
};
